'use client';

// Framework-light Cloudflare Turnstile — the twin of Turnstile.client.tsx for
// component trees that are ALSO bundled by esbuild into the embed IIFEs
// (widget/build.mjs): the booking flow and the embeddable contact form.
//
// Unlike the in-app twin it cannot use next/script, so it injects the api.js
// loader into document.head once (deduped), polls for window.turnstile, then
// renders explicitly into a ref — explicit rendering is also what makes the
// challenge work inside a shadow root (cfb-booking pattern). It behaves
// identically on a hosted Next page; next/script is a nicety, not a
// requirement. The Window.turnstile global type comes from Turnstile.client.tsx.
//
// Tokens are SINGLE USE. After the token has been spent (or rejected) the
// parent must remount this component (change its `key`) to get a fresh one.

import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptRequested = false;

function ensureScript(): void {
  if (scriptRequested) return;
  scriptRequested = true;
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
  const s = document.createElement('script');
  s.src = SCRIPT_SRC;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

export interface TurnstileProps {
  siteKey: string;
  locale: string;
  /** Called with the token, or '' when it expires or the widget errors. */
  onToken: (token: string) => void;
}

export function Turnstile({ siteKey, locale, onToken }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Keep the latest callback without retriggering the challenge on parent
  // re-renders (same pattern as the in-app component).
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    ensureScript();
    let cancelled = false;
    let widgetId: string | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tryRender = () => {
      if (cancelled) return;
      const el = containerRef.current;
      if (window.turnstile && el) {
        widgetId = window.turnstile.render(el, {
          sitekey: siteKey,
          language: locale,
          theme: 'light',
          callback: (token: string) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => onTokenRef.current(''),
        });
        return;
      }
      timer = setTimeout(tryRender, 100);
    };
    tryRender();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // noop
        }
      }
    };
  }, [siteKey, locale]);

  return <div ref={containerRef} />;
}
